import React from 'react'
import ReactSlider from 'react-slider'
import css from './Slider.less'
import useEvent from '../hooks/useEvent'
import {EMPTY_OBJECT} from '../helpers/constants'
import assert from 'assert'
import cn from 'classnames'
import {clamp, parseFloat} from '../util/math'

interface SliderUi {
    /** @deprecated */
    handle?: never
    handleIndex: number
    value: number
    values: number[]
}

/**
 * @see https://api.jqueryui.com/slider
 */
export interface JQuerySliderOptions {
    value: StringNum
    disabled?: boolean
    min?: number
    max?: number
    step?: number
}

type FixedOptions = RequiredKeys<ReactSliderOptions, 'min'|'max'>

export interface ReactSliderOptions extends JQuerySliderOptions {
    onSlide?: (ev: unknown, ui: SliderUi) => void
}

function fixOptions(options: ReactSliderOptions) {
    if(options.min == null) {
        // http://api.jqueryui.com/slider/#option-min
        options.min = 0
    }
    if(options.max == null) {
        options.max = 10000
    }
    return options as FixedOptions
}

export default function Slider(options: ReactSliderOptions) {
    const {value,disabled,min,max,step,onSlide} = fixOptions({...options})
    const onChange = useEvent((value,index) => {
        onSlide?.(EMPTY_OBJECT, {
            handleIndex: index,
            value,
            values: [value],
        })
    })
    const hasValue = value != null && value !== '';
    const fixedValue = clamp(parseFloat(value,min),min,max)
    return <ReactSlider className={css.slider} value={fixedValue} disabled={disabled} thumbClassName={cn(css.thumb,!hasValue && css.noValue)} trackClassName={css.track} min={min} max={max} step={step} onChange={onChange}/>
}

export interface MultiSliderProps {
    values: StringNum[]
    disabled?: boolean
    min: number
    max: number
    step?: number
    minDistance?: number
    /** Number of handles/knobs/thumbs */
    handles?: number
    onChange(ev: SliderUi): void
}

export function MultiSlider({values,disabled,min,max,minDistance,step,onChange,handles=3}: MultiSliderProps) {
    assert(max >= min)
    assert(handles >= 1)
    let fixedValues: number[]
    let hasValue = false
    if(values?.length && values.some(v => v != null && v !== '')) {
        assert(values.length === handles)
        fixedValues = values.map(v => clamp(parseFloat(v, min),min,max))
        hasValue = true
    } else {
        fixedValues = [min]
        if(handles > 1) {
            if(handles > 2) {
                let curr = min
                const mid = handles - 2
                const space = (max - min) / (handles - 1);
                for(let i = 0; i < mid; ++i) {
                    curr += space
                    fixedValues.push(curr)
                }
            }
            fixedValues.push(max)
        }
    }
    const changeHandler = useEvent((values, index) => {
        onChange?.({
            handleIndex: index,
            value: values[index],
            values: values,
        })
    })
    return <ReactSlider className={css.slider} value={fixedValues} disabled={disabled} thumbClassName={cn(css.thumb,!hasValue && css.noValue)} trackClassName={css.track} min={min} max={max} step={step} minDistance={minDistance} pearling onChange={changeHandler}/>
}
